import {
	Button,
	Card,
	FormControl,
	FormLabel,
	Select,
	Stack,
	Text,
	Textarea,
} from '@chakra-ui/react';
import { ThemeProvider } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import InputField from 'components/fields/InputField';
import dayjs from 'dayjs';
import { displayNumericString, extractNumericValue } from 'utils/format';

export default function Report({
	BRANDS,
	formData,
	setFormData,
	message,
	loading,
	handleFocus,
	handleSubmit,
	textColorPrimary,
	inputBorder,
	cardBg,
	brandColor,
	theme,
}) {
	return (
		<Card bg={cardBg} boxShadow='md' mb='20px' p={6}>
			<Text mb='20px'>
				Si ya hiciste una compra y no ves tu cashback reflejado, puedes
				reportarlo aquí. Recuerda esperar al menos <strong>7 días</strong> desde
				la compra para enviar esta solicitud.
			</Text>
			<form onSubmit={handleSubmit}>
				<Stack spacing={4}>
					{/* Tienda */}
					<FormControl isRequired>
						<FormLabel
							display='flex'
							ms='10px'
							htmlFor='brand'
							fontSize='sm'
							color={textColorPrimary}
							fontWeight='bold'
							_hover={{ cursor: 'pointer' }}
						>
							Tienda
						</FormLabel>
						<Select
							name='brand'
							value={formData.brand}
							borderColor={message.status === 'error' ? 'red.500' : inputBorder}
							borderWidth={message.status === 'error' ? 2 : null}
							bg={cardBg}
							variant='main'
							cursor='pointer'
							fontWeight='500'
							fontSize={'14px'}
							h='44px'
							maxh='44px'
							onChange={(e) => {
								const selectedId = e.target.value;
								const selectedBrand = BRANDS.find(
									(brand) => brand.id.toString() === selectedId
								);

								setFormData((prev) => ({
									...prev,
									brandId: selectedId,
									_brandId: {
										image: selectedBrand?.image || '',
										brandName: selectedBrand?.brandName || '',
									},
								}));
							}}
						>
							<option value=''>Selecciona una tienda</option>
							{BRANDS.map((brand) => (
								<option key={brand.id} value={brand.id}>
									{brand.brandName}
								</option>
							))}
						</Select>
					</FormControl>
					{/* Fecha */}
					<FormControl isRequired>
						<FormLabel
							display='flex'
							ms='10px'
							htmlFor='brand'
							fontSize='sm'
							color={textColorPrimary}
							fontWeight='bold'
							_hover={{ cursor: 'pointer' }}
						>
							Fecha de compra
						</FormLabel>
						<ThemeProvider theme={theme}>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DatePicker
									value={
										formData.buyDate
											? dayjs(formData.buyDate, 'YYYY-MM-DD')
											: dayjs()
									}
									onChange={(newValue) =>
										setFormData((prev) => ({
											...prev,
											date: newValue ? newValue.format('YYYY-MM-DD') : '',
										}))
									}
									slotProps={{
										textField: {
											fullWidth: true,
											InputProps: {
												sx: {
													height: '44px',
													border: `1px solid ${inputBorder}`,
													borderRadius: '16px',
													'&:hover': {
														borderColor: inputBorder,
													},
													fontSize: '14px',
													fontWeight: '500',
												},
											},
											sx: {
												margin: 0,
												// Remove fieldset outline
												'& .MuiOutlinedInput-root': {
													'& fieldset': {
														border: 'none',
													},
												},
											},
										},
									}}
								/>
							</LocalizationProvider>
						</ThemeProvider>
					</FormControl>
					{/* Orden */}
					<FormControl isRequired>
						<InputField
							borderColor={message.status === 'error' ? 'red.500' : inputBorder}
							borderWidth={message.status === 'error' ? 2 : null}
							bg={cardBg}
							mb='0px'
							id='order'
							label='Orden'
							placeholder='Ej. #1406'
							_focus={{ borderWidth: 2, borderColor: brandColor }}
							value={formData.order}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									order: e.target.value,
								}))
							}
							onFocus={handleFocus}
						/>
					</FormControl>
					{/* Monto */}
					<FormControl isRequired>
						<InputField
							borderColor={message.status === 'error' ? 'red.500' : inputBorder}
							borderWidth={message.status === 'error' ? 2 : null}
							bg={cardBg}
							mb='0px'
							id='amount'
							label='Monto (antes de impuestos y envíos)'
							placeholder='Ej. 489.99'
							_focus={{ borderWidth: 2, borderColor: brandColor }}
							value={displayNumericString(formData.amount)}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									amount: extractNumericValue(e.target.value),
								}))
							}
							onFocus={handleFocus}
							unit='MXN'
						/>
					</FormControl>
					<FormControl>
						<FormLabel
							display='flex'
							ms='10px'
							htmlFor='brand'
							fontSize='sm'
							color={textColorPrimary}
							fontWeight='bold'
							_hover={{ cursor: 'pointer' }}
						>
							Notas
						</FormLabel>
						<Textarea
							borderColor={message.status === 'error' ? 'red.500' : inputBorder}
							borderWidth={message.status === 'error' ? 2 : null}
							borderRadius={'16px'}
							bg={cardBg}
							mb='0px'
							id='notes'
							label='Notas (opcional)'
							_focus={{
								borderWidth: 2,
								borderColor: brandColor,
								boxShadow: 'none',
							}}
							value={formData.notes || ''}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									notes: e.target.value,
								}))
							}
							onFocus={handleFocus}
							rows={4}
						/>
					</FormControl>
				</Stack>
				<Button
					isLoading={loading}
					isDisabled={
						!formData.amount ||
						!formData.buyDate ||
						!formData.brandId ||
						!formData.order
					}
					variant='brand'
					size='lg'
					mt={10}
					onClick={handleSubmit}
				>
					Enviar disputa
				</Button>
			</form>
		</Card>
	);
}
